import React, { useEffect, useMemo, useState } from "react";
import {
  createDepartment,
  deleteDepartment,
  fetchCompanies,
  fetchDepartments,
  updateDepartment,
} from "../../services/organization";
import "./organization.css";

const initialForm = {
  company_id: "",
  name: "",
};

function normalizeList(response) {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.data?.data)) {
    return response.data.data;
  }

  return [];
}

export default function DepartmentsPage() {
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  async function loadData() {
    setLoading(true);
    setError("");

    try {
      const [departmentResponse, companyResponse] = await Promise.all([
        fetchDepartments(),
        fetchCompanies(),
      ]);

      setItems(normalizeList(departmentResponse));
      setCompanies(normalizeList(companyResponse));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load departments.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  const companyMap = useMemo(() => {
    return companies.reduce((acc, company) => {
      acc[String(company.id)] = company.name;
      return acc;
    }, {});
  }, [companies]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return items;
    }

    return items.filter((item) =>
      [item?.name, companyMap[String(item?.company_id)]]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [items, search, companyMap]);

  function resetForm() {
    setForm(initialForm);
    setEditingId(null);
  }

  function handleEdit(item) {
    setEditingId(item.id);
    setForm({
      company_id: item?.company_id || "",
      name: item?.name || "",
    });
    setSuccess("");
    setError("");
  }

  async function handleDelete(id) {
    const confirmed = window.confirm("Delete this department record?");
    if (!confirmed) {
      return;
    }

    try {
      await deleteDepartment(id);
      setSuccess("Department deleted successfully.");
      if (editingId === id) {
        resetForm();
      }
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to delete department.");
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const payload = {
        ...form,
        company_id: form.company_id || null,
      };

      if (editingId) {
        await updateDepartment(editingId, payload);
        setSuccess("Department updated successfully.");
      } else {
        await createDepartment(payload);
        setSuccess("Department created successfully.");
      }

      resetForm();
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save department.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="org-page">
      <section className="org-card org-page__header">
        <div>
          <span className="org-page__eyebrow">Organisation Setup</span>
          <h1>Departments</h1>
          <p>Maintain departments used in staff records, payroll processing, and reporting.</p>
        </div>

        <div className="org-page__toolbar">
          <input
            className="org-page__search"
            type="text"
            placeholder="Search departments..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {editingId ? (
            <button
              type="button"
              className="org-page__button org-page__button--light"
              onClick={resetForm}
            >
              Cancel Edit
            </button>
          ) : null}
        </div>
      </section>

      {error ? <div className="org-error">{error}</div> : null}
      {success ? <div className="org-success">{success}</div> : null}

      <section className="org-card org-page__content">
        <form className="org-form org-form--grid" onSubmit={handleSubmit}>
          <div className="org-field">
            <label htmlFor="department_company">Organization</label>
            <select
              id="department_company"
              value={form.company_id}
              onChange={(e) => setForm((prev) => ({ ...prev, company_id: e.target.value }))}
              required
            >
              <option value="">Select organization</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>

          <div className="org-field">
            <label htmlFor="department_name">Department Name</label>
            <input
              id="department_name"
              type="text"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Enter department name"
              required
            />
          </div>

          <div className="org-form__actions org-field--full">
            <button className="org-page__button" type="submit" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Update Department" : "Create Department"}
            </button>
          </div>
        </form>
      </section>

      <section className="org-card org-page__content">
        {loading ? (
          <div className="org-loading">Loading departments...</div>
        ) : filteredItems.length === 0 ? (
          <div className="org-empty">No department records found.</div>
        ) : (
          <div className="org-table-wrap">
            <table className="org-table">
              <thead>
                <tr>
                  <th>Department</th>
                  <th>Organization</th>
                  <th style={{ width: 160 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="org-table__title">{item?.name || "-"}</div>
                    </td>
                    <td>{companyMap[String(item?.company_id)] || item?.company_id || "-"}</td>
                    <td>
                      <div className="org-actions">
                        <button
                          type="button"
                          className="org-action-btn"
                          onClick={() => handleEdit(item)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="org-action-btn org-action-btn--danger"
                          onClick={() => handleDelete(item.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
